import React from 'react';
import SettlementList from '../SettlementList';
import SettlementListSearchBox from '../SettlementListSearchBox';
import SettlementListSummaryBox from '../SettlementSummaryBox';
import { StyledContainer } from './styles';
import useRouter from '../../../../hooks/common/useRouter';
import { LIST_PAGINATION_MODEL } from '../../../../store/modules/app/models';
import { isSettlementForm } from '../../utils/guards';
import { ColumnFiltersState, OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import { convertSettlementFormToParams } from '../../utils';
import { isSortingState, isColumnFiltersState } from '../../../../components/TanstackTable';

type Props = { type: 'ready' | 'completed' | 'hold' };

const INITIAL_PAGE = LIST_PAGINATION_MODEL.pageNum + 1;
const INITIAL_PAGE_SIZE = LIST_PAGINATION_MODEL.pageSize;

const SettlementListWrap = ({ type }: Props): JSX.Element => {
  const router = useRouter();
  const { query, pathname } = router;

  const page = !isNaN(Number(query.page)) ? Number(query.page) : INITIAL_PAGE;
  const pageSize = !isNaN(Number(query.pageSize)) ? Number(query.pageSize) : INITIAL_PAGE_SIZE;

  const params = isSettlementForm(query) ? query : null;

  const parsedSorting = typeof query.sort === 'string' ? JSON.parse(query.sort) : query.sort;
  const parsedFiltering = typeof query.filtering === 'string' ? JSON.parse(query.filtering) : query.filtering;

  const sorting = isSortingState(parsedSorting) ? parsedSorting : [{ id: 'id', desc: true }];

  const filtering = isColumnFiltersState(parsedFiltering) ? parsedFiltering : [];

  const pagination = {
    pageIndex: page - 1,
    pageSize: pageSize,
  };

  const [sort] = sorting;

  const request = params
    ? convertSettlementFormToParams({ form: params, sort, filter: filtering, pagination, status: type })
    : null;

  const handlePagination: OnChangeFn<PaginationState> = (updaterOrValue) => {
    const newPagination = typeof updaterOrValue === 'function' ? updaterOrValue(pagination) : updaterOrValue;

    router.push(pathname, {
      query: {
        ...router.query,
        page: String(newPagination.pageIndex + 1),
        pageSize: String(newPagination.pageSize),
      },
    });
  };

  const handleFilteringChange: OnChangeFn<ColumnFiltersState> = (updaterOrValue) => {
    const newFiltering = typeof updaterOrValue === 'function' ? updaterOrValue(filtering) : updaterOrValue;

    router.push(pathname, {
      query: {
        ...router.query,
        filtering: JSON.stringify(newFiltering),
        page: String(INITIAL_PAGE),
        pageSize: String(INITIAL_PAGE_SIZE - 1),
      },
    });
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;

    router.push(pathname, {
      query: {
        ...router.query,
        sort: JSON.stringify(newSorting),
      },
    });
  };

  const handleResetFilter = (): void => {
    router.push(pathname, {
      query: {
        ...router.query,
        sort: JSON.stringify([{ id: 'id', desc: true }]),
        filtering: JSON.stringify([]),
      },
    });
  };

  return (
    <StyledContainer>
      {request ? <SettlementListSummaryBox request={request} /> : <SettlementListSummaryBox.Placeholder />}
      <SettlementListSearchBox />
      {request ? (
        <SettlementList
          onResetFilter={handleResetFilter}
          type={type}
          sortingState={[sorting, handleSortingChange]}
          filteringState={[filtering, handleFilteringChange]}
          paginationState={[pagination, handlePagination]}
          request={request}
        />
      ) : (
        <SettlementList.Placeholder
          type={type}
          sortingState={[sorting, handleSortingChange]}
          filteringState={[filtering, handleFilteringChange]}
          paginationState={[pagination, handlePagination]}
        />
      )}
    </StyledContainer>
  );
};

export default SettlementListWrap;
